import React, { useMemo } from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { Accordion, Text } from '@weave/design-system';
import {
  ChecklistMetricItem,
  ChecklistMetricStatus,
  MultiLocationChecklistMetricItem,
} from '../../handoff-checklist-v2.types';
import { MetricTable } from './metric-table';
import { getStatusIcon } from '../../utils/status-icon';
import { ReactComponent as CrossSquareBorderedIcon } from 'images/cross-square-bordered.svg';
import {
  ExceptionChangeParams,
  MultiLocationMetricTable,
} from './multi-location-metric-table';
import { StatusBadge } from './status-badge';

const isMultiLocationMetric = (
  metric: ChecklistMetricItem | MultiLocationChecklistMetricItem
): metric is MultiLocationChecklistMetricItem =>
  'locationResults' in metric && metric.locationResults.length > 0;

export type MetricAccordionProps = {
  defaultOpen?: boolean;
  readOnly?: boolean;
  showWhereToFix?: boolean;
} & (
  | {
      onLocationExceptionChange?: (params: ExceptionChangeParams) => void;
      onExceptionChange?: never;
      metric: MultiLocationChecklistMetricItem;
    }
  | {
      metric: ChecklistMetricItem;
      onLocationExceptionChange?: never;
      onExceptionChange?: (
        metricId: string,
        criteriaIndex: number,
        exception: string
      ) => void;
    }
);

export const MetricAccordion = ({
  metric,
  defaultOpen = false,
  readOnly = false,
  showWhereToFix = true,
  onExceptionChange,
  onLocationExceptionChange,
}: MetricAccordionProps) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const headerStatusIcon = useMemo(() => {
    if (metric.status === ChecklistMetricStatus.BLOCKED) {
      return <CrossSquareBorderedIcon css={styles.statusIcon} />;
    }
    const StatusIcon = getStatusIcon(metric.status);

    return StatusIcon ? <StatusIcon css={styles.statusIcon} /> : null;
  }, [metric.status]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const locationResults = isMultiLocationMetric(metric)
    ? metric.locationResults
    : undefined;

  return (
    <Accordion
      css={styles.accordionContainer}
      controlledValue={isOpen ? metric.id : ''}
      onChange={handleToggle}
      showBoxShadow={false}
    >
      <Accordion.Item value={metric.id}>
        <Accordion.Header>
          <div css={styles.headerContent}>
            <div css={styles.leftSection}>
              {headerStatusIcon}
              <Text weight="medium" as="div" css={styles.metricNameText}>
                {metric.name}
              </Text>
            </div>
            <div css={styles.rightSection}>
              <StatusBadge
                status={metric.status}
                isLoading={metric.isLoading}
                locationResults={locationResults}
                allowExceptions={metric.allowExceptions}
              />
            </div>
          </div>
        </Accordion.Header>
        <Accordion.Body css={styles.body}>
          {isMultiLocationMetric(metric) ? (
            <MultiLocationMetricTable
              metricId={metric.id}
              allowExceptions={metric.allowExceptions}
              locationResults={metric.locationResults}
              showWhereToFix={showWhereToFix}
              readOnly={readOnly}
              onExceptionChange={onLocationExceptionChange}
            />
          ) : (
            <MetricTable
              criterias={metric.criteria}
              metricId={metric.id}
              allowExceptions={metric.allowExceptions}
              showWhereToFix={showWhereToFix}
              readOnly={readOnly}
              onExceptionChange={onExceptionChange}
            />
          )}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

const styles = {
  accordionContainer: css`
    margin-bottom: 0;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    overflow: hidden;

    /* Override internal Accordion.Item styles */
    & > div,
    & > [class*='Item'],
    & > section {
      border: none;
      box-shadow: none;
      margin: 0;
    }

    /* Override internal Accordion.Header button and active states */
    button,
    [class*='Header'] button {
      &:focus {
        outline: none;
      }

      &[data-active='true'] {
        background-color: #dcebfe;
      }
    }
  `,
  headerContent: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-right: ${theme.spacing(2)};
  `,
  leftSection: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
  `,
  statusIcon: css`
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  metricNameText: css`
    font-size: 16px;
    color: #1f1f1f;
  `,
  rightSection: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
  `,
  body: css`
    background-color: #ffffff;
    border: none;
    padding: ${theme.spacing(2)};
  `,
};
